import _defineProperty from "/vercel/path0/apps/corona/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/vercel/path0/apps/corona/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _toConsumableArray from "/vercel/path0/apps/corona/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { ApolloProvider } from '@apollo/client';
import { ChakraProvider, HStack, Stack } from '@chakra-ui/react';
import { client as PackagesApiClient } from '@cofit/api';
import '@cofit/calendar/dist/FullCalendar.css';
import { GoogleTagManager } from '@next/third-parties/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { appWithTranslation, withTranslation } from 'next-i18next';
import { useRouter } from 'next/dist/client/router';
import dynamic from 'next/dynamic';
import 'rc-calendar/assets/index.css';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { RecoilRoot } from 'recoil';
import 'rsuite/dist/rsuite-no-reset.min.css';
import { AnalysisGuide } from 'src/components/AnalysisGuide';
import { Menu as AnalysisGuideMenu } from 'src/components/AnalysisGuide/Menu';
import { Provider as TopicGuideContextProvider } from 'src/components/TopicGuide/TopicGuideContext';
import ChatSharedWorkerProvider from 'src/context/ChatSharedWorkerProvider';
import { SideBarContextProvider, useSideBarContext } from 'src/context/SideBarContextProvider';
import { MenuID, Module } from 'src/types';
import 'tailwindcss/tailwind.css';
import { useLocalStorage } from 'usehooks-ts';
import Error404 from '../src/components/Error404';
import ErrorBoundary from '../src/components/ErrorBoundary';
import Head from '../src/components/Head';
import ShellWithGroupedMenu from '../src/components/ShellWithGroupedMenu/ShellWithGroupedMenu';
import { BranchConstants, GTM_AUTH, GTM_ID, GTM_PREVIEW, SHOW_SIDE_BAR_LOCAL_STORAGE_KEY, commonSettings, getModuleFromPathname, updateOrgInfoOnClient } from '../src/constants';
import { sentryConfig } from '../src/constants/sentry';
import UserDataContextProvider, { useUserDataContextProvider } from '../src/context/UserDataContextProvider';
import { client } from '../src/lib/ApolloClient';
import '../src/lib/Calendar.css';
import { RouteAuthorizer, analysisGuideIDs, instantFoodToolIDs } from '../src/lib/auth/RouteAuthorizer';
import '../src/lib/re-calendar-tweak.css';
import { setUpSentry } from '../src/lib/sentry';
import '../styles/globals.css';
import { getTheme } from '../theme';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'china') {
  // for cn environment, use the REST API origin from environment variables directly and not the proxy by next.js
  PackagesApiClient["default"].setBaseUrl(process.env.NEXT_PUBLIC_REST_API_ORIGIN);
} // Dynamic imports


var _DefaultHeader = dynamic(function () {
  return import('../src/components/DefaultHeader');
}, {
  ssr: false,
  loadableGenerated: {
    webpack: function webpack() {
      return [require.resolveWeak('../src/components/DefaultHeader')];
    }
  }
});

var DefaultHeader = withTranslation(['common'])(_DefaultHeader); // Constants

var queryClient = new QueryClient(); // Sidebar configuration constants

var SIDEBAR_CONFIG = {
  COLLAPSED_WIDTH: 20,
  EXPANDED_WIDTHS: {
    lg: '20vw',
    xl: '15vw'
  },
  DISABLED_PATHS: [MenuID.login]
};

var FixedLayout = function FixedLayout(_ref) {
  var children = _ref.children,
      pathname = _ref.pathname,
      expandSideBar = _ref.expandSideBar,
      setExpandSideBar = _ref.setExpandSideBar;
  var isAnalysisGuide = useMemo(function () {
    return analysisGuideIDs.includes(pathname);
  }, [pathname]);
  var isInstantFoodTool = useMemo(function () {
    return instantFoodToolIDs.includes(pathname);
  }, [pathname]);
  var showDefaultHeader = useMemo(function () {
    return ![].concat(_toConsumableArray(analysisGuideIDs), [MenuID.login]).includes(pathname);
  }, [pathname]);

  var _useSideBarContext = useSideBarContext(),
      sideBarContainerRef = _useSideBarContext.sideBarContainerRef;

  var sideBarConfig = useMemo(function () {
    var isDisabled = SIDEBAR_CONFIG.DISABLED_PATHS.includes(pathname) || isInstantFoodTool;
    var isExpandable = !isAnalysisGuide;
    var width = isDisabled ? 0 : isExpandable && expandSideBar ? SIDEBAR_CONFIG.EXPANDED_WIDTHS : SIDEBAR_CONFIG.COLLAPSED_WIDTH;
    return {
      enabled: !isDisabled,
      expandable: isExpandable,
      width: width
    };
  }, [pathname, isAnalysisGuide, isInstantFoodTool, expandSideBar]);
  return /*#__PURE__*/_jsxs(HStack, {
    spacing: "0",
    bg: "#F5F5F5",
    alignItems: "stretch",
    w: "full",
    h: "100vh",
    overflow: "hidden",
    pos: "relative",
    e2eid: "pages__app_HStack",
    children: [commonSettings.isDevelopmentEnv && /*#__PURE__*/_jsx(Stack, {
      w: "100%",
      h: "3px",
      bgColor: "cyan.500",
      position: "absolute",
      top: 0,
      zIndex: 9999,
      e2eid: "pages__app_Stack"
    }), commonSettings.isTestingEnv && /*#__PURE__*/_jsx(Stack, {
      w: "100%",
      h: "3px",
      bgColor: "pink.400",
      position: "absolute",
      top: 0,
      zIndex: 9999,
      e2eid: "pages__app_Stack_2"
    }), sideBarConfig.enabled && /*#__PURE__*/_jsx(Stack, {
      layerStyle: "base",
      overflowY: "auto",
      borderRightWidth: "1px",
      borderColor: "gray.200",
      e2eid: "pages__app_Stack_3",
      children: isAnalysisGuide ? /*#__PURE__*/_jsx(AnalysisGuideMenu, {
        sideBarWidth: sideBarConfig.width,
        e2eid: "pages__app_AnalysisGuideMenu"
      }) : /*#__PURE__*/_jsx(ShellWithGroupedMenu, {
        sideBarWidth: sideBarConfig.width,
        setShowSideBar: setExpandSideBar,
        showSideBar: expandSideBar,
        e2eid: "pages__app_ShellWithGroupedMenu"
      })
    }), /*#__PURE__*/_jsxs(Stack, {
      flex: "1",
      spacing: 0,
      overflow: "hidden",
      h: "full",
      e2eid: "pages__app_Stack",
      children: [showDefaultHeader && /*#__PURE__*/_jsx(Suspense, {
        fallback: /*#__PURE__*/_jsx(Stack, {
          h: "60px",
          w: "full",
          e2eid: "pages__app_Stack"
        }),
        e2eid: "pages__app_Suspense",
        children: /*#__PURE__*/_jsx(DefaultHeader, {
          showLogo: isInstantFoodTool,
          showBannerTitle: !isInstantFoodTool,
          e2eid: "pages__app_DefaultHeader"
        })
      }), /*#__PURE__*/_jsx(ErrorBoundary, {
        e2eid: "pages__app_ErrorBoundary",
        children: /*#__PURE__*/_jsx(HStack, {
          ref: sideBarContainerRef,
          flex: "1",
          alignItems: "stretch",
          overflow: "auto",
          spacing: 1,
          e2eid: "pages__app_HStack",
          children: /*#__PURE__*/_jsx(Stack, {
            flex: "1",
            minH: 0 // Ensure it respects flex constraints
            ,
            overflowY: "auto" // Scroll if content exceeds height
            ,
            e2eid: "pages__app_Stack",
            children: isAnalysisGuide ? /*#__PURE__*/_jsx(AnalysisGuide, {
              e2eid: "pages__app_AnalysisGuide",
              children: children
            }) : children
          })
        })
      })]
    })]
  });
};

var AuthenticatedContent = function AuthenticatedContent(_ref2) {
  var children = _ref2.children,
      pathname = _ref2.pathname;

  var _useUserDataContextPr = useUserDataContextProvider(),
      role = _useUserDataContextPr.role,
      authenticated = _useUserDataContextPr.authenticated;

  var _useRouter = useRouter(),
      push = _useRouter.push;

  var rolesReady = useMemo(function () {
    return authenticated && role.length > 0;
  }, [authenticated, role]);
  var isAuthorized = useMemo(function () {
    return RouteAuthorizer.getInstance().isPathnameAuthorized({
      roles: role,
      pathname: pathname
    });
  }, [role, pathname]);
  var isNon404Path = useMemo(function () {
    return [MenuID.login, '/'].includes(pathname);
  }, [pathname]);
  useEffect(function () {
    if (rolesReady && !isAuthorized && pathname === '/') {
      push(RouteAuthorizer.getInstance().getDefaultPath({
        roles: role
      }));
    }
  }, [rolesReady, isAuthorized, pathname, push, role]);
  if (!authenticated) return pathname === MenuID.login ? children : null;
  if (!rolesReady) return null;
  return isAuthorized ? children : isNon404Path ? null : /*#__PURE__*/_jsx(Error404, {
    e2eid: "pages__app_Error404"
  });
};

function MyApp(_ref3) {
  var Component = _ref3.Component,
      pageProps = _ref3.pageProps;

  var _useRouter2 = useRouter(),
      pathname = _useRouter2.pathname;

  var _useLocalStorage = useLocalStorage(SHOW_SIDE_BAR_LOCAL_STORAGE_KEY, true),
      _useLocalStorage2 = _slicedToArray(_useLocalStorage, 2),
      expandSideBar = _useLocalStorage2[0],
      setExpandSideBar = _useLocalStorage2[1];

  var _useState = useState(true),
      isLoading = _useState[0],
      setIsLoading = _useState[1];

  useEffect(function () {
    // ensure org & branch constants are set prior to rendering
    updateOrgInfoOnClient();

    if (BranchConstants.SITE_WISE_ENABLE_SENTRY && sentryConfig.enableSentry) {
      window.sentryOnLoad = function () {
        setUpSentry();
      };
    }

    setIsLoading(false);
  }, []);
  var theme = useMemo(function () {
    var isAnalysisGuide = analysisGuideIDs.includes(pathname);
    var isInstantFoodTool = instantFoodToolIDs.includes(pathname);
    var moduleType = isAnalysisGuide || isInstantFoodTool ? Module.consult : getModuleFromPathname(pathname);
    return getTheme({
      moduleType: moduleType
    });
  }, [pathname]);
  if (isLoading) return null;

  if (pathname === MenuID._3DModel) {
    return /*#__PURE__*/_jsx(Component, _objectSpread(_objectSpread({}, pageProps), {}, {
      e2eid: "pages__app_Component"
    }));
  }

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(RecoilRoot, {
      e2eid: "pages__app_RecoilRoot",
      children: /*#__PURE__*/_jsx(UserDataContextProvider, {
        e2eid: "pages__app_UserDataContextProvider",
        children: /*#__PURE__*/_jsx(ApolloProvider, {
          client: client,
          e2eid: "pages__app_ApolloProvider",
          children: /*#__PURE__*/_jsx(QueryClientProvider, {
            client: queryClient,
            e2eid: "pages__app_QueryClientProvider",
            children: /*#__PURE__*/_jsxs(TopicGuideContextProvider, {
              e2eid: "pages__app_TopicGuideContextProvider",
              children: [/*#__PURE__*/_jsx(Head, {
                e2eid: "pages__app_Head"
              }), /*#__PURE__*/_jsx(ChakraProvider, {
                theme: theme,
                e2eid: "pages__app_ChakraProvider",
                children: /*#__PURE__*/_jsx(SideBarContextProvider, {
                  e2eid: "pages__app_SideBarContextProvider",
                  children: /*#__PURE__*/_jsx(ChatSharedWorkerProvider, {
                    e2eid: "pages__app_ChatSharedWorkerProvider",
                    children: /*#__PURE__*/_jsx(FixedLayout, {
                      pathname: pathname,
                      expandSideBar: expandSideBar,
                      setExpandSideBar: setExpandSideBar,
                      e2eid: "pages__app_FixedLayout",
                      children: /*#__PURE__*/_jsx(Suspense, {
                        fallback: "Loading...",
                        e2eid: "pages__app_Suspense",
                        children: /*#__PURE__*/_jsx(AuthenticatedContent, {
                          pathname: pathname,
                          e2eid: "pages__app_AuthenticatedContent",
                          children: /*#__PURE__*/_jsx(Component, _objectSpread(_objectSpread({}, pageProps), {}, {
                            e2eid: "pages__app_Component"
                          }))
                        })
                      })
                    })
                  })
                })
              })]
            })
          })
        })
      })
    }), /*#__PURE__*/_jsx(GoogleTagManager, {
      gtmId: GTM_ID,
      preview: GTM_PREVIEW,
      auth: GTM_AUTH,
      e2eid: "pages__app_GoogleTagManager"
    })]
  });
}

export default appWithTranslation(MyApp);